import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Settings as SettingsIcon, Loader2, Save,
  Download, Bell, Calendar, Sparkles, CalendarPlus,
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import ProfileSection from "@/components/settings/ProfileSection";
import SubscriptionSection from "@/components/settings/SubscriptionSection";
import WellnessModeSection from "@/components/settings/WellnessModeSection";
import DigestSection from "@/components/settings/DigestSection";
import DataExportSection from "@/components/settings/DataExportSection";
import LanguageSection from "@/components/settings/LanguageSection";
import LogoutSection from "@/components/settings/LogoutSection";

const Settings = () => {
  const { user, token, logout, subscription, refreshSubscription } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [cycleLength, setCycleLength] = useState("28");
  const [periodLength, setPeriodLength] = useState("5");
  
  // Subscription side data (subscription itself comes from AuthContext)
  const [tiers, setTiers] = useState({});
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [changingTier, setChangingTier] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  
  // Notifications
  const [notifSettings, setNotifSettings] = useState({
    period_reminder_days: 3,
    fertile_window_reminder: true,
    moon_phase_alerts: true,
    meditation_reminders: true,
    meditation_reminder_time: "20:00",
    streak_alerts: true,
    enabled: false
  });
  const [pushEnabled, setPushEnabled] = useState(false);
  const [enablingPush, setEnablingPush] = useState(false);
  
  // Calendar integration
  const [calendarUrls, setCalendarUrls] = useState([]);
  
  // Digest settings
  const [digestSettings, setDigestSettings] = useState({
    weekly_digest_enabled: false,
    digest_day: "monday",
    email: "",
    cycle_reminders_enabled: true,
    monthly_summary_enabled: true,
    fertility_digest_enabled: true,
  });
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingFertilityDigest, setSendingFertilityDigest] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchAllSettings();
    fetchPaypalClientId();
    
    // Check for payment success
    const sessionId = searchParams.get("session_id");
    const success = searchParams.get("success");
    if (sessionId && success) {
      checkPaymentStatus(sessionId);
    }
    
    // Check for PayPal success
    const paypalSuccess = searchParams.get("paypal");
    const payerId = searchParams.get("PayerID");
    const paymentId = searchParams.get("paymentId");
    if (paypalSuccess === "success" && payerId && paymentId) {
      executePaypalPayment(paymentId, payerId);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPaypalClientId = async () => {
    try {
      const res = await axios.get(`${API}/paypal/client-id`);
      setPaypalClientId(res.data.client_id);
    } catch (error) {
      // settings fallback to defaults if fetch fails
    }
  };

  const executePaypalPayment = async (paymentId, payerId) => {
    setCheckingPayment(true);
    try {
      const res = await axios.post(`${API}/paypal/execute`, {
        payment_id: paymentId,
        payer_id: payerId
      }, { headers, withCredentials: true });
      
      if (res.data.status === "success") {
        toast.success("Welcome to Sacred Celestial Flow Premium! ✨");
        fetchAllSettings();
        refreshSubscription();
        navigate("/settings", { replace: true });
      }
    } catch (error) {
      toast.error("PayPal payment failed. Please try again.");
      // settings fallback to defaults if fetch fails
    } finally {
      setCheckingPayment(false);
    }
  };

  const fetchAllSettings = async () => {
    try {
      const [settingsRes, tiersRes, notifRes, calRes, historyRes, pushStatusRes, digestRes] = await Promise.all([
        axios.get(`${API}/settings`, { headers, withCredentials: true }),
        axios.get(`${API}/subscription/tiers`),
        axios.get(`${API}/notifications/settings`, { headers, withCredentials: true }),
        axios.get(`${API}/calendar/google-url`, { headers, withCredentials: true }).catch(() => ({ data: { urls: [] } })),
        axios.get(`${API}/subscription/history`, { headers, withCredentials: true }).catch(() => ({ data: { payment_history: [] } })),
        axios.get(`${API}/notifications/subscription-status`, { headers, withCredentials: true }).catch(() => ({ data: { subscribed: false } })),
        axios.get(`${API}/digest/settings`, { headers, withCredentials: true }).catch(() => ({ data: { weekly_digest_enabled: false, digest_day: "monday", email: "" } }))
      ]);
      
      if (settingsRes.data) {
        setCycleLength(settingsRes.data.cycle_length?.toString() || "28");
        setPeriodLength(settingsRes.data.period_length?.toString() || "5");
      }
      setTiers(tiersRes.data.tiers || {});
      setNotifSettings(notifRes.data);
      setCalendarUrls(calRes.data.urls || []);
      setPaymentHistory(historyRes.data.payment_history || []);
      setPushEnabled(pushStatusRes.data.subscribed || false);
      setDigestSettings(digestRes.data);
    } catch (error) {
      // settings fallback to defaults if fetch fails
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    try {
      // Poll for payment status
      for (let i = 0; i < 5; i++) {
        const res = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, { headers, withCredentials: true });
        if (res.data.status === "completed") {
          toast.success("Welcome to Sacred Celestial Flow Premium! ✨");
          fetchAllSettings();
          refreshSubscription();
          // Clear URL params
          navigate("/settings", { replace: true });
          break;
        }
        await new Promise(r => setTimeout(r, 2000));
      }
    } catch (error) {
      // settings fallback to defaults if fetch fails
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings`, {
        cycle_length: parseInt(cycleLength),
        period_length: parseInt(periodLength)
      }, { headers, withCredentials: true });
      toast.success("Settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    try {
      await axios.post(`${API}/notifications/settings`, notifSettings, { headers, withCredentials: true });
      toast.success("Notification settings saved!");
    } catch (error) {
      toast.error("Failed to save notification settings");
    }
  };

  const handleSaveDigest = async () => {
    try {
      await axios.post(`${API}/digest/settings`, digestSettings, { headers, withCredentials: true });
      toast.success("Digest settings saved!");
    } catch (error) {
      toast.error("Failed to save digest settings");
    }
  };

  const handleSendTestDigest = async () => {
    setSendingDigest(true);
    try {
      const email = digestSettings.email || user?.email;
      const res = await axios.post(`${API}/digest/send-test`, { email }, { headers, withCredentials: true });
      toast.success(`Test digest sent to ${email}`);
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to send digest";
      toast.error(detail);
    } finally {
      setSendingDigest(false);
    }
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post(`${API}/digest/send`, {}, { headers, withCredentials: true });
      toast.success(res.data.message || "Digest sent!");
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to send digest";
      toast.error(detail);
    } finally {
      setSendingDigest(false);
    }
  };

  const handleSendFertilityDigestNow = async () => {
    setSendingFertilityDigest(true);
    try {
      const res = await axios.post(`${API}/digest/fertility/send-test`, {}, { headers, withCredentials: true });
      toast.success(`Fertility digest sent to ${res.data.to || user?.email}`);
      // Refresh digest settings so "Last sent" line updates immediately
      try {
        const refreshed = await axios.get(`${API}/digest/settings`, { headers, withCredentials: true });
        setDigestSettings(refreshed.data);
      } catch { /* non-fatal */ }
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to send fertility digest";
      toast.error(detail);
    } finally {
      setSendingFertilityDigest(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const res = await axios.get(`${API}/reports/cycle-report`, {
        headers, withCredentials: true, responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `sacred_celestial_flow_report_${new Date().toISOString().split("T")[0]}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF report downloaded!");
    } catch (error) {
      toast.error("Failed to generate PDF report");
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    toast.success(lng === "es" ? "Idioma cambiado a Espanol" : "Language changed to English");
  };

  const handleSubscribe = async (tierId) => {
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        tier_id: tierId,
        origin_url: window.location.origin
      }, { headers, withCredentials: true });
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    }
  };

  const handleExport = async (format) => {
    try {
      const res = await axios.get(`${API}/export/data?format=${format}`, { headers, withCredentials: true });
      
      const blob = new Blob([format === "csv" ? res.data : JSON.stringify(res.data, null, 2)], {
        type: format === "csv" ? "text/csv" : "application/json"
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sacred_celestial_flow_export.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success("Data exported successfully!");
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const handleExportIcal = async () => {
    try {
      const res = await axios.get(`${API}/calendar/export-ical`, { 
        headers, 
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/calendar' }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "sacred_celestial_flow_cycle.ics";
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success("Calendar file downloaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export calendar");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const enablePushNotifications = async () => {
    setEnablingPush(true);
    try {
      // Check if service workers are supported
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        toast.error("Push notifications are not supported in this browser");
        return;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error("Please allow notifications to receive reminders");
        return;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      await navigator.serviceWorker.ready;

      // Get VAPID public key
      const vapidRes = await axios.get(`${API}/notifications/vapid-key`);
      const vapidPublicKey = vapidRes.data.publicKey;

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      await axios.post(`${API}/notifications/subscribe`, {
        subscription: subscription.toJSON()
      }, { headers, withCredentials: true });

      setPushEnabled(true);
      toast.success("Push notifications enabled! 🔔");
    } catch (error) {
      // settings fallback to defaults if fetch fails
      toast.error("Failed to enable push notifications");
    } finally {
      setEnablingPush(false);
    }
  };

  const disablePushNotifications = async () => {
    try {
      // Unregister from backend
      await axios.delete(`${API}/notifications/unsubscribe`, { headers, withCredentials: true });

      // Unsubscribe from push manager
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }

      setPushEnabled(false);
      toast.success("Push notifications disabled");
    } catch (error) {
      // settings fallback to defaults if fetch fails
      toast.error("Failed to disable push notifications");
    }
  };

  const sendTestNotification = async () => {
    try {
      const res = await axios.post(`${API}/notifications/send-test`, {}, { headers, withCredentials: true });
      if (res.data.result?.sent) {
        toast.success("Test notification sent!");
      } else {
        toast.error(res.data.message || "Failed to send test notification");
      }
    } catch (error) {
      toast.error("Failed to send test notification");
    }
  };

  const sendMeditationReminder = async () => {
    try {
      const res = await axios.post(`${API}/notifications/send-meditation-reminder`, {}, { headers, withCredentials: true });
      if (res.data.result?.sent) {
        toast.success("Meditation reminder sent!");
      } else {
        toast.info(res.data.message || "Reminder not sent");
      }
    } catch (error) {
      toast.error("Failed to send reminder");
    }
  };

  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      const res = await axios.post(`${API}/subscription/cancel`, {}, { headers, withCredentials: true });
      toast.success(res.data.message);
      setShowCancelConfirm(false);
      fetchAllSettings();
      refreshSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      const res = await axios.post(`${API}/subscription/reactivate`, {}, { headers, withCredentials: true });
      toast.success(res.data.message);
      fetchAllSettings();
      refreshSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate subscription");
    }
  };

  const handleChangeTier = async (newTier) => {
    setChangingTier(true);
    try {
      const res = await axios.post(`${API}/subscription/change-tier`, { new_tier: newTier }, { headers, withCredentials: true });
      toast.success(res.data.message);
      fetchAllSettings();
      refreshSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change subscription tier");
    } finally {
      setChangingTier(false);
    }
  };

  const handleCancelTierChange = async () => {
    try {
      await axios.delete(`${API}/subscription/cancel-tier-change`, { headers, withCredentials: true });
      toast.success("Pending tier change cancelled");
      fetchAllSettings();
      refreshSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel tier change");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="settings-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <SettingsIcon className="w-8 h-8 text-mystic-300" />
          Settings
        </h1>
        <p className="text-mystic-200/70">Customize your Sacred Celestial Flow experience</p>
      </motion.div>

      {/* Profile */}
      <ProfileSection user={user} isPremium={!!subscription?.is_premium} />

      {/* Subscription */}
      <SubscriptionSection
        subscription={subscription}
        tiers={tiers}
        checkingPayment={checkingPayment}
        selectedTier={selectedTier}
        setSelectedTier={setSelectedTier}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        paypalClientId={paypalClientId}
        showCancelConfirm={showCancelConfirm}
        setShowCancelConfirm={setShowCancelConfirm}
        cancellingSubscription={cancellingSubscription}
        changingTier={changingTier}
        paymentHistory={paymentHistory}
        headers={headers}
        onSubscribe={handleSubscribe}
        onCancelSubscription={handleCancelSubscription}
        onReactivateSubscription={handleReactivateSubscription}
        onChangeTier={handleChangeTier}
        onCancelTierChange={handleCancelTierChange}
      />

      {/* Wellness Mode */}
      <WellnessModeSection />

      {/* Cycle Settings */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Card className="glass">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Cycle Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-mystic-100">Average Cycle Length</Label>
                <Select value={cycleLength} onValueChange={setCycleLength}>
                  <SelectTrigger className="bg-mystic-900/50 border-white/10 text-white" data-testid="cycle-length-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-mystic-800 border-white/10">
                    {Array.from({ length: 15 }, (_, i) => i + 21).map((days) => (
                      <SelectItem key={days} value={days.toString()} className="text-white hover:bg-mystic-700">
                        {days} days
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-mystic-100">Average Period Length</Label>
                <Select value={periodLength} onValueChange={setPeriodLength}>
                  <SelectTrigger className="bg-mystic-900/50 border-white/10 text-white" data-testid="period-length-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-mystic-800 border-white/10">
                    {Array.from({ length: 8 }, (_, i) => i + 2).map((days) => (
                      <SelectItem key={days} value={days.toString()} className="text-white hover:bg-mystic-700">
                        {days} days
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-mystic-300 hover:bg-mystic-200 text-white rounded-full" data-testid="save-settings-btn">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save Settings</>}
            </Button>
          </CardContent>
        </Card>
      </motion.div>

      {/* Notifications */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
        <Card className="glass">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Push Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Push Notification Enable/Disable */}
            <div className="glass-light rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-white font-medium">Push Notifications</p>
                  <p className="text-mystic-200/70 text-sm">Receive reminders on your device</p>
                </div>
                {pushEnabled ? (
                  <Button 
                    onClick={disablePushNotifications}
                    variant="outline"
                    size="sm"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    Disable
                  </Button>
                ) : (
                  <Button 
                    onClick={enablePushNotifications}
                    disabled={enablingPush}
                    size="sm"
                    className="bg-moon-gold hover:bg-moon-gold/90 text-mystic-900"
                    data-testid="enable-push-btn"
                  >
                    {enablingPush ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
                    Enable
                  </Button>
                )}
              </div>
              {pushEnabled && (
                <div className="flex gap-2 mt-2">
                  <Button 
                    onClick={sendTestNotification}
                    variant="outline"
                    size="sm"
                    className="border-white/20 text-mystic-300 hover:bg-mystic-700 text-xs"
                  >
                    Send Test
                  </Button>
                  <Button 
                    onClick={sendMeditationReminder}
                    variant="outline"
                    size="sm"
                    className="border-white/20 text-mystic-300 hover:bg-mystic-700 text-xs"
                  >
                    Test Meditation Reminder
                  </Button>
                </div>
              )}
            </div>

            {/* Notification Preferences Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white">Enable Notification Preferences</p>
                <p className="text-mystic-200/70 text-sm">Customize what reminders you receive</p>
              </div>
              <Switch
                checked={notifSettings.enabled}
                onCheckedChange={(checked) => setNotifSettings({...notifSettings, enabled: checked})}
                data-testid="notifications-toggle"
              />
            </div>
            
            {notifSettings.enabled && (
              <>
                {/* Meditation Reminders */}
                <div className="glass-light rounded-xl p-4 border border-orange-500/20">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-white font-medium flex items-center gap-2">
                        <span className="text-orange-400">🧘</span> Meditation Reminders
                      </p>
                      <p className="text-mystic-200/70 text-sm">Daily reminders to keep your streak</p>
                    </div>
                    <Switch
                      checked={notifSettings.meditation_reminders}
                      onCheckedChange={(checked) => setNotifSettings({...notifSettings, meditation_reminders: checked})}
                    />
                  </div>
                  {notifSettings.meditation_reminders && (
                    <div className="space-y-2">
                      <Label className="text-mystic-100 text-sm">Reminder Time</Label>
                      <Select 
                        value={notifSettings.meditation_reminder_time || "20:00"} 
                        onValueChange={(v) => setNotifSettings({...notifSettings, meditation_reminder_time: v})}
                      >
                        <SelectTrigger className="bg-mystic-900/50 border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-mystic-800 border-white/10">
                          {["06:00", "07:00", "08:00", "09:00", "12:00", "18:00", "19:00", "20:00", "21:00", "22:00"].map((time) => (
                            <SelectItem key={time} value={time} className="text-white hover:bg-mystic-700">
                              {time.replace(":00", "")} {parseInt(time) < 12 ? "AM" : parseInt(time) === 12 ? "PM" : parseInt(time) > 12 ? `${parseInt(time) - 12} PM` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Streak Alerts */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-mystic-100 flex items-center gap-2">
                      <span className="text-orange-400">🔥</span> Streak Alerts
                    </p>
                    <p className="text-mystic-200/60 text-xs">Alert when streak is at risk</p>
                  </div>
                  <Switch
                    checked={notifSettings.streak_alerts}
                    onCheckedChange={(checked) => setNotifSettings({...notifSettings, streak_alerts: checked})}
                  />
                </div>

                {/* Period Reminders */}
                <div className="space-y-2">
                  <Label className="text-mystic-100">Period Reminder (days before)</Label>
                  <Select 
                    value={(notifSettings.period_reminder_days || 3).toString()} 
                    onValueChange={(v) => setNotifSettings({...notifSettings, period_reminder_days: parseInt(v)})}
                  >
                    <SelectTrigger className="bg-mystic-900/50 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-mystic-800 border-white/10">
                      {[1, 2, 3, 5, 7].map((days) => (
                        <SelectItem key={days} value={days.toString()} className="text-white hover:bg-mystic-700">
                          {days} {days === 1 ? "day" : "days"} before
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center justify-between">
                  <p className="text-mystic-100">Fertile Window Reminders</p>
                  <Switch
                    checked={notifSettings.fertile_window_reminder}
                    onCheckedChange={(checked) => setNotifSettings({...notifSettings, fertile_window_reminder: checked})}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <p className="text-mystic-100">Moon Phase Alerts</p>
                  <Switch
                    checked={notifSettings.moon_phase_alerts}
                    onCheckedChange={(checked) => setNotifSettings({...notifSettings, moon_phase_alerts: checked})}
                  />
                </div>
                
                <Button onClick={handleSaveNotifications} variant="outline" className="w-full border-white/20 text-white hover:bg-mystic-700 rounded-full">
                  Save Notification Settings
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Calendar Integration */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}>
        <Card className="glass">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-moon-gold" />
              Calendar Integration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-mystic-200/70 text-sm">Sync your cycle predictions with your calendar.</p>
            
            <div className="flex flex-wrap gap-3">
              <Button onClick={handleExportIcal} variant="outline" className="border-white/20 text-white hover:bg-mystic-700 rounded-full" data-testid="export-ical-btn">
                <Download className="w-4 h-4 mr-2" /> Download .ics File
              </Button>
            </div>
            
            {calendarUrls.length > 0 && (
              <div className="space-y-2 mt-4">
                <p className="text-mystic-100 text-sm">Quick add to Google Calendar:</p>
                {calendarUrls.slice(0, 2).map((cal, i) => (
                  <div key={`k-${i}`} className="glass-light rounded-lg p-3">
                    <p className="text-mystic-300 text-xs mb-2">Cycle starting {cal.cycle_start}</p>
                    <div className="flex flex-wrap gap-2">
                      <a href={cal.period_url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline" className="border-cycle-menstrual/30 text-cycle-menstrual hover:bg-cycle-menstrual/10 text-xs">
                          Add Period
                        </Button>
                      </a>
                      <a href={cal.ovulation_url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline" className="border-moon-gold/30 text-moon-gold hover:bg-moon-gold/10 text-xs">
                          Add Ovulation
                        </Button>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Weekly Digest Email */}
      <DigestSection
        digestSettings={digestSettings}
        setDigestSettings={setDigestSettings}
        user={user}
        sendingDigest={sendingDigest}
        sendingFertilityDigest={sendingFertilityDigest}
        onSave={handleSaveDigest}
        onSendTest={handleSendTestDigest}
        onSendFertilityNow={handleSendFertilityDigestNow}
      />

      {/* Data Export */}
      <DataExportSection onExport={handleExport} onDownloadPDF={handleDownloadPDF} />

      {/* Language */}
      <LanguageSection currentLang={i18n.language} onChange={changeLanguage} />

      {/* Logout */}
      <LogoutSection onLogout={handleLogout} />
    </div>
  );
};

export default Settings;
